import { ThemedText } from '@/components/themed-text';
import { buildServicePreview } from '@/components/services/wizard/build-preview';
import { PreviewDock } from '@/components/services/wizard/preview-dock';
import {
  StepAgenda,
  StepPrice,
  StepPublish,
  StepSaleMode,
} from '@/components/services/wizard/steps';
import { WizardTrack } from '@/components/services/wizard/wizard-track';
import { PageLayout } from '@/components/ui/page-layout';
import { Switch } from '@/components/ui/switch';
import { useMembership } from '@/hooks/use-membership';
import { useServiceCatalog } from '@/hooks/use-service-catalog';
import { useCreateService, useService, useUpdateService } from '@/hooks/use-services';
import {
  useCreateServiceTemplate,
  useServiceTemplates,
  type ServiceTemplate,
} from '@/hooks/use-service-templates';
import { emptyServiceForm, serviceSchema, type ServiceFormData } from '@/lib/validators/service';
import { showToast } from '@/store/toastStore';
import { PRICE_DISPLAY_MODE } from '@/types/price-display-mode';
import { useActingProviderStore } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import { getApiErrorMessage } from '@/utils/api-error';
import { canEditCatalogIdentity } from '@/utils/catalog-permissions';
import {
  serviceFormToTemplateDto,
  serviceToForm,
  templateToServiceForm,
} from '@/utils/service-template-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ActivityIndicator, View } from 'react-native';

const STEPS = [
  { label: 'Modelo' },
  { label: 'Preço' },
  { label: 'Agenda' },
  { label: 'Publicação' },
];

type ServiceField = keyof ServiceFormData;

/** Em que passo cada campo mora — para levar o erro até onde se resolve. */
const FIELD_STEP: Partial<Record<ServiceField, number>> = {
  defaultPrice: 2,
  priceDisplayMode: 2,
  allowedPaymentMethods: 2,
  durationMinutes: 3,
  slotCapacity: 3,
  locationMode: 3,
  address: 3,
  subscriptionQuota: 3,
  subscriptionQuotaPeriod: 3,
  recurringHorizonDays: 3,
  recurringBillingInterval: 3,
  name: 4,
  description: 4,
};

/** O mesmo mapa lido ao contrário: o que cada passo precisa ter respondido. */
const STEP_FIELDS = Object.entries(FIELD_STEP).reduce<Record<number, ServiceField[]>>(
  (acc, [field, step]) => {
    acc[step] = [...(acc[step] ?? []), field as ServiceField];
    return acc;
  },
  {},
);

interface Props {
  /**
   * Presente = edição: carrega o serviço, faz PATCH e o passo final salva em vez
   * de publicar. Ausente = criação (com `fromServiceId`/`fromTemplateId` como
   * ponto de partida).
   */
  serviceId?: string;
}

/**
 * Criar E editar serviço na MESMA tela — o assistente de quatro perguntas com o
 * cartão do cliente atualizando embaixo a cada resposta. O modo muda só pela
 * presença de `serviceId`: criação publica, edição salva.
 *
 * O formulário inteiro numa página escondia a decisão que muda tudo — como o
 * cliente compra — no meio de campos de mesmo peso. Aqui ela é a primeira
 * pergunta, e o resto do assistente se ajusta a ela.
 */
export function ServiceFormScreen({ serviceId }: Props) {
  const isEdit = !!serviceId;

  const { fromServiceId, fromTemplateId } = useLocalSearchParams<{
    fromServiceId?: string;
    fromTemplateId?: string;
  }>();

  // Edição parte do próprio serviço; criação por cópia parte do serviço de origem.
  const sourceServiceId = serviceId ?? fromServiceId;
  const { data: serviceData } = useService(sourceServiceId);
  const { data: templatesData } = useServiceTemplates();

  const [step, setStep] = useState(1);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    trigger,
    getFieldState,
    formState: { isSubmitting, errors },
  } = useForm<ServiceFormData>({
    resolver: zodResolver(serviceSchema),
    // Assistente não responde no lugar de ninguém: a agenda abre em branco e
    // cada campo espera uma escolha. Só o "entra na agenda" já vem ligado,
    // porque é o caso de quase todo serviço e desligar é um toque.
    defaultValues: {
      ...emptyServiceForm,
      durationMinutes: null,
      subscriptionQuota: undefined,
      subscriptionQuotaPeriod: undefined,
    },
  });

  const createServiceMutation = useCreateService();
  const updateServiceMutation = useUpdateService();
  const createTemplateMutation = useCreateServiceTemplate();

  const values = useWatch({ control }) as ServiceFormData;

  // Só na criação: num serviço que já existe, mudar o nome é do catálogo, na
  // tela do item.
  const { membership, isOwner } = useMembership();
  const isTeam = membership?.type === 'TEAM';
  const { data: catalog = [] } = useServiceCatalog({ enabled: isTeam });
  const catalogNames = serviceId
    ? []
    : catalog.filter((item) => !item.mine).map((item) => item.name);

  const acting = useActingProviderStore((s) => s.acting);
  const selfProviderId = useAuthStore((s) => s.user?.providerProfile?.id ?? null);
  const focusedProviderId = acting?.providerId ?? selfProviderId;
  /**
   * Nome e descrição de um serviço da equipe valem para todos que o fazem: o
   * profissional edita o preço e a agenda da cópia dele, mas o texto do serviço
   * da barbearia é de quem criou (ou do dono). A API recusa do mesmo jeito.
   */
  const catalogItemId = isEdit ? (serviceData?.catalogItemId ?? null) : null;
  const identityLocked = useMemo(() => {
    if (!isTeam || !catalogItemId) return false;
    const item = catalog.find((entry) => entry.id === catalogItemId);
    if (!item) return !isOwner;
    return !canEditCatalogIdentity(item, { isOwner, providerId: focusedProviderId });
  }, [isTeam, catalogItemId, catalog, isOwner, focusedProviderId]);
  const { summary, preview } = useMemo(() => buildServicePreview(values), [values]);

  // Quem escolheu mostrar o valor na página pública precisa ter um valor —
  // "sob consulta" é o caminho de quem combina caso a caso.
  const priceDisplay = values.priceDisplayMode;
  useEffect(() => {
    setValue('hasFixedPrice', priceDisplay !== PRICE_DISPLAY_MODE.HIDDEN);
  }, [priceDisplay, setValue]);

  const applyTemplate = useCallback(
    (template: ServiceTemplate) => {
      reset(templateToServiceForm(template));
    },
    [reset],
  );

  useEffect(() => {
    if (isEdit || !fromTemplateId || !templatesData) return;
    const all = [...(templatesData.system ?? []), ...(templatesData.provider ?? [])];
    const template = all.find((item) => item.id === fromTemplateId);
    if (template) applyTemplate(template);
  }, [isEdit, fromTemplateId, templatesData, applyTemplate]);

  // Editar: prefill com o serviço salvo. Duplicar: copia a configuração inteira,
  // inclusive agendamento. Os dois partem do mesmo mapeamento (`serviceToForm`).
  useEffect(() => {
    if (!sourceServiceId || !serviceData) return;
    reset(serviceToForm(serviceData));
  }, [sourceServiceId, serviceData, reset]);

  const submitLabel = isEdit ? 'Salvar' : 'Publicar serviço';
  const submitTestID = isEdit ? 'service-edit-submit-btn' : 'service-create-submit-btn';

  const persist = async (data: ServiceFormData) => {
    const payload = { ...data, hasFixedPrice: !!data.defaultPrice?.trim() };
    try {
      if (isEdit && serviceId) {
        await updateServiceMutation.mutateAsync({ id: serviceId, data: payload });
        showToast(`${data.name} salvo.`, 'success');
        router.replace(`/(tabs)/(provider)/services/${serviceId}`);
        return;
      }
      const service = await createServiceMutation.mutateAsync(payload);
      if (saveAsTemplate) {
        try {
          await createTemplateMutation.mutateAsync(serviceFormToTemplateDto(data));
        } catch (e) {
          console.warn('Falha ao salvar modelo:', e);
        }
      }
      router.replace(`/(tabs)/(provider)/services/${service.id}`);
    } catch (error) {
      showToast(getApiErrorMessage(error, 'Não foi possível salvar o serviço.'), 'error');
    }
  };

  // Erro num passo que já ficou para trás não pode virar botão mudo: leva de
  // volta até o campo e diz o que falta.
  const handleInvalid = (invalid: Record<string, { message?: string }>) => {
    const [field] = Object.keys(invalid);
    const target = FIELD_STEP[field as ServiceField];
    if (target) setStep(target);
    const message = field ? invalid[field]?.message : undefined;
    showToast(message || 'Confira os campos destacados.', 'error');
  };

  const messageOf = (fields: ServiceField[]) =>
    fields
      .map((field) => getFieldState(field).error?.message ?? errors[field]?.message)
      .find(Boolean);

  /**
   * Cada passo é um portão: só passa quem já respondeu o que aquele passo pede.
   * Deixar seguir e reclamar no fim empurraria o erro para longe do campo que o
   * resolve — e o assistente inteiro existe para evitar isso.
   */
  const validateStep = async (target: number) => {
    const fields = STEP_FIELDS[target] ?? [];
    if (fields.length === 0) return true;
    const valid = await trigger(fields);
    if (!valid) {
      setStep(target);
      showToast(messageOf(fields) || 'Confira os campos destacados.', 'error');
    }
    return valid;
  };

  /** Voltar é livre; avançar exige que todo passo no caminho esteja em pé. */
  const goToStep = async (target: number) => {
    if (target <= step) {
      setStep(target);
      return;
    }
    for (let current = step; current < target; current += 1) {
      if (!(await validateStep(current))) return;
    }
    setStep(target);
  };

  const handleAction = async () => {
    if (step < STEPS.length) {
      await goToStep(step + 1);
      return;
    }
    await handleSubmit(persist, handleInvalid)();
  };

  const handleBack = () => {
    if (isEdit && serviceId) {
      router.replace(`/(tabs)/(provider)/services/${serviceId}`);
      return;
    }
    if (router.canGoBack()) router.back();
    else router.replace('/(tabs)/(provider)/services');
  };

  const title = isEdit ? 'Editar serviço' : fromServiceId ? 'Copiar serviço' : 'Novo serviço';

  const submitting =
    isSubmitting || createServiceMutation.isPending || updateServiceMutation.isPending;

  if (sourceServiceId && !serviceData) {
    return (
      <View className="flex-1 items-center justify-center bg-canvas">
        <ActivityIndicator size="large" className="text-primary" />
        <ThemedText className="mt-2 text-foreground">Carregando serviço...</ThemedText>
      </View>
    );
  }

  const saveTemplateFooter = isEdit ? null : (
    <View
      className="flex-row items-center gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={{ borderCurve: 'continuous' }}
    >
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-sm text-neutral-900">
          Salvar como modelo
        </ThemedText>
        <ThemedText className="text-xs leading-4 text-neutral-500">
          Guarda este preenchimento para os próximos serviços.
        </ThemedText>
      </View>
      <Switch checked={saveAsTemplate} onCheckedChange={setSaveAsTemplate} />
    </View>
  );

  return (
    <PageLayout
      title={title}
      subtitle={`Passo ${step} de ${STEPS.length}`}
      onBack={handleBack}
      backDisabled={submitting}
      scrollProps={{
        keyboardShouldPersistTaps: 'handled',
        showsVerticalScrollIndicator: false,
      }}
      footer={
        <PreviewDock
          summary={summary}
          preview={preview}
          actionLabel={step < STEPS.length ? 'Continuar' : submitLabel}
          actionTestID={step < STEPS.length ? 'service-wizard-next' : submitTestID}
          loading={submitting}
          onBack={step > 1 ? () => setStep(step - 1) : undefined}
          onAction={() => void handleAction()}
        />
      }
    >
      <WizardTrack steps={STEPS} current={step} onSelect={(target) => void goToStep(target)} />

      {step === 1 ? <StepSaleMode control={control} setValue={setValue} /> : null}
      {step === 2 ? <StepPrice control={control} setValue={setValue} /> : null}
      {step === 3 ? <StepAgenda control={control} setValue={setValue} /> : null}
      {step === 4 ? (
        <StepPublish
          control={control}
          footer={saveTemplateFooter}
          catalogNames={catalogNames}
          onPickCatalogName={(name) => setValue('name', name, { shouldDirty: true })}
          identityLocked={identityLocked}
        />
      ) : null}
    </PageLayout>
  );
}
